import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { StatusPill } from "@/components/admin/StatusPill";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Sparkles, ShieldCheck, Rocket, ArchiveIcon, CheckCircle2, ExternalLink, Undo2 } from "lucide-react";
import { ContentTab } from "./editor/ContentTab";
import { WebPreviewTab } from "./editor/WebPreviewTab";
import { SocialTab } from "./editor/SocialTab";
import { SEOTab } from "./editor/SEOTab";
import { SafetyTab } from "./editor/SafetyTab";
import { RevisionsTab } from "./editor/RevisionsTab";

// v3.1 5-state workflow (no external sources anymore)
const ALLOWED_ACTIONS = {
  draft: ["save_draft", "generate_draft", "regenerate_section", "run_safety_check", "mark_ready", "archive"],
  in_review: ["save_draft", "regenerate_section", "run_safety_check", "approve", "back_to_draft", "archive"],
  approved: ["publish", "back_to_in_review", "archive"],
  published: ["back_to_in_review", "archive"],
  archived: [],
};

export default function AdminIssueEditorPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [issue, setIssue] = useState(null);
  const [revisions, setRevisions] = useState([]);
  const [actionCount, setActionCount] = useState(0);
  const [tab, setTab] = useState("content");
  const [me, setMe] = useState(null);

  const load = async () => {
    try {
      const r = await api.get(`/admin/issues/${id}`);
      setIssue(r.data.issue);
      setRevisions(r.data.revisions || []);
      setActionCount(r.data.action_count || 0);
    } catch (e) {
      toast.error("Could not load issue");
      nav("/admin/issues");
    }
  };

  useEffect(() => { load(); api.get("/admin/me").then(r => setMe(r.data)); /* eslint-disable-next-line */ }, [id]);

  if (!issue) {
    return <AdminLayout><div className="h-40 rounded-xl bg-secondary animate-pulse" /></AdminLayout>;
  }

  const allowed = ALLOWED_ACTIONS[issue.status] || [];
  const canGenerate = allowed.includes("generate_draft");
  const canSafety = allowed.includes("run_safety_check");
  const canMarkReady = allowed.includes("mark_ready");
  const canApprove = allowed.includes("approve") && issue.safety_status !== "failed" && issue.safety_status !== "pending";
  const canPublish = allowed.includes("publish");
  const canRevert = allowed.includes("back_to_in_review");
  const canArchive = allowed.includes("archive");
  const canEdit = issue.status !== "archived" && issue.status !== "published";

  const save = async () => {
    if (!canEdit) {
      toast.error("Move issue back to in_review before editing");
      return;
    }
    try {
      const payload = { ...issue };
      delete payload.id; delete payload.created_at; delete payload.updated_at;
      // Strip any lingering legacy field so the request body stays clean.
      delete payload.connection_in_the_world;
      const r = await api.patch(`/admin/issues/${id}`, payload);
      setIssue(r.data);
      toast.success("Saved");
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || "" });
    }
  };

  const doAction = async (action, payload = {}) => {
    try {
      const r = await api.post(`/admin/issues/${id}/action`, { action, payload });
      if (r.data?.issue) setIssue(r.data.issue);
      if (r.data?.blockers) {
        toast.error("Cannot proceed", { description: r.data.blockers.slice(0, 3).join("; ") });
        return r.data;
      }
      const label = action.replace(/_/g, " ");
      toast.success(`${label} done`);
      load();
      return r.data;
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === "string" ? d : (d?.blockers ? d.blockers.join("; ") : "Action failed");
      toast.error("Action failed", { description: msg });
    }
  };

  return (
    <AdminLayout>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <div className="font-mono text-xs text-muted-foreground">Issue #{String(issue.issue_number).padStart(3, "0")}</div>
            <StatusPill status={issue.status} />
            {issue.safety_status && issue.safety_status !== "pending" && (
              <span className={`text-[11px] px-2 py-0.5 rounded-full border ${issue.safety_status === "failed" ? "border-destructive text-destructive" : issue.safety_status === "warning" ? "border-[color:var(--coh-warning)] text-[color:var(--coh-warning)]" : "border-[color:var(--coh-success)] text-[color:var(--coh-success)]"}`}>
                Safety: {issue.safety_status}
              </span>
            )}
          </div>
          <h1 data-testid="editor-title" className="mt-2 font-display text-3xl font-bold">{issue.title || "(Untitled)"}</h1>
          {actionCount > 0 && (
            <div data-testid="editor-action-count" className="mt-2 inline-flex items-center gap-2 rounded-full border border-[color:var(--coh-blue)]/30 bg-[color:var(--coh-blue)]/5 px-3 py-1 text-xs">
              <span className="font-mono font-semibold text-[color:var(--coh-blue)]">{new Intl.NumberFormat().format(actionCount)}</span>
              <span className="text-muted-foreground">humans reached out from this issue</span>
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={() => doAction("generate_draft", { theme: issue.theme || "" })} disabled={!canGenerate} data-testid="action-generate-draft">
            <Sparkles className="h-3.5 w-3.5 mr-2" /> Generate draft
          </Button>
          <Button size="sm" variant="outline" onClick={() => doAction("run_safety_check")} disabled={!canSafety} data-testid="action-safety">
            <ShieldCheck className="h-3.5 w-3.5 mr-2" /> Safety check
          </Button>
          <Button size="sm" variant="outline" onClick={() => doAction("mark_ready")} disabled={!canMarkReady} data-testid="action-mark-ready">
            Mark ready
          </Button>
          <Button size="sm" onClick={() => doAction("approve")} disabled={!canApprove} data-testid="action-approve" className="bg-[color:var(--coh-success)] text-white hover:opacity-90">
            <CheckCircle2 className="h-3.5 w-3.5 mr-2" /> Approve
          </Button>
          <Button size="sm" onClick={() => doAction("publish")} disabled={!canPublish} data-testid="action-publish" className="bg-[color:var(--coh-blue)] text-white hover:bg-[color:var(--coh-blue)]/90">
            <Rocket className="h-3.5 w-3.5 mr-2" /> Publish
          </Button>
          {canRevert && (
            <Button size="sm" variant="outline" onClick={() => doAction("back_to_in_review")} data-testid="action-back-to-in-review">
              <Undo2 className="h-3.5 w-3.5 mr-2" /> {issue.status === "published" ? "Revert to in review (correction)" : "Back to in review"}
            </Button>
          )}
          {(issue.status === "published") && (
            <Button asChild size="sm" variant="ghost"><a href={`/issues/${issue.slug}`} target="_blank" rel="noopener noreferrer"><ExternalLink className="h-3.5 w-3.5 mr-2" /> View public</a></Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => doAction("archive")} disabled={!canArchive} data-testid="action-archive">
            <ArchiveIcon className="h-3.5 w-3.5 mr-2" /> Archive
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mt-6" data-testid="admin-issue-editor-tabs">
        <TabsList className="flex flex-wrap gap-1 h-auto bg-secondary p-1">
          <TabsTrigger value="content" data-testid="tab-content">Content</TabsTrigger>
          <TabsTrigger value="preview" data-testid="tab-preview">Web Preview</TabsTrigger>
          <TabsTrigger value="social" data-testid="tab-social">Social</TabsTrigger>
          <TabsTrigger value="seo" data-testid="tab-seo">SEO</TabsTrigger>
          <TabsTrigger value="safety" data-testid="tab-safety">Safety</TabsTrigger>
          <TabsTrigger value="revisions" data-testid="tab-revisions">Revisions</TabsTrigger>
        </TabsList>

        <TabsContent value="content" className="mt-6">
          <ContentTab issue={issue} setIssue={setIssue} onSave={save} mockLlm={me?.mock_llm} readOnly={!canEdit} />
        </TabsContent>
        <TabsContent value="preview" className="mt-6">
          <WebPreviewTab issue={issue} />
        </TabsContent>
        <TabsContent value="social" className="mt-6">
          <SocialTab issue={issue} setIssue={setIssue} onSave={save} />
        </TabsContent>
        <TabsContent value="seo" className="mt-6">
          <SEOTab issue={issue} setIssue={setIssue} onSave={save} />
        </TabsContent>
        <TabsContent value="safety" className="mt-6">
          <SafetyTab issue={issue} setIssue={setIssue} />
        </TabsContent>
        <TabsContent value="revisions" className="mt-6">
          <RevisionsTab revisions={revisions} />
        </TabsContent>
      </Tabs>
    </AdminLayout>
  );
}
